/**
 * Historical Session Browser Store
 *
 * Zustand store for session list and transcript state management.
 * Handles data fetching, URL synchronization, selection, and lazy transcript loading.
 *
 * Task 2561 — STREAM_UX_PATTERNS.md §1.2 Master-Detail (Browser flavor)
 */

import { create } from 'zustand'
import { ApiClientError } from '@/lib/api/client'
import {
  listSessions,
  getTranscriptChunk,
  getSessionArtifacts,
  getSessionTrace,
  listAgents,
  resolveSession,
  type AgentOption,
} from './api'
import type {
  SessionListItem,
  SessionFilters,
  SessionSortState,
  SessionInspectorTab,
  TranscriptCache,
  TranscriptSearchState,
  TranscriptTurnItem,
  TimeBucketGroup,
  SessionArtifact,
  SessionStatus,
  SessionSource,
} from './types'
import { groupSessionsByBucket } from './types'

// ── Constants ───────────────────────────────────────────────────────────────

const TRANSCRIPT_PAGE_SIZE = 50
const LIST_PAGE_SIZE = 100
/** 60 second polling interval for historical surface */
const POLL_INTERVAL_MS = 60_000
/**
 * Number of most-recent sessions to pre-warm transcript cache for after the
 * initial list load.  These are the sessions the operator is most likely to
 * click immediately, so pre-fetching the first transcript chunk eliminates
 * the loading spinner on first open.  Set to 0 to disable.
 */
const PRE_WARM_TRANSCRIPT_COUNT = 3

// ── Store State ─────────────────────────────────────────────────────────────

interface SessionsStoreState {
  // List state
  sessions: SessionListItem[]
  /**
   * Client-side narrowed view of ``sessions`` after re-applying the active
   * filters in-process.  This is what the list view renders; ``sessions``
   * holds the raw page-merged data from the server so we can re-narrow on
   * filter changes without re-fetching, and so the "X of Y loaded"
   * indicator can show both numbers.
   *
   * Mirrors the Automations store's ``applyClientFilters`` pattern (task
   * 3201e).  The server-side ``q`` parameter is still passed on the wire
   * (it narrows the candidate pool), but the client-side filter is
   * authoritative for what the operator sees — protecting against the
   * Load-More-grows-with-junk bug where new pages bypass the search term.
   */
  filteredSessions: SessionListItem[]
  groupedSessions: TimeBucketGroup[]
  cursor: string | null
  hasMore: boolean
  filters: SessionFilters
  sortState: SessionSortState

  // Selection
  selectedId: string | null

  // Comparison mode (task 2621)
  comparisonIds: [string, string] | null
  comparisonScrollLinked: boolean

  // Detail / transcript state (keyed by sessionId)
  transcriptCache: Map<string, TranscriptCache>
  artifactsCache: Map<string, { artifacts: SessionArtifact[]; status: 'idle' | 'loading' | 'loaded' | 'error'; error?: string }>
  traceCache: Map<string, { data: Record<string, unknown> | null; status: 'idle' | 'loading' | 'loaded' | 'error'; error?: string }>

  // Inspector state
  activeTab: SessionInspectorTab
  transcriptSearch: TranscriptSearchState

  // Loading states
  listLoading: boolean
  listError: string | null
  loadingMore: boolean

  // Agents facet
  agents: AgentOption[]
  agentsLoading: boolean

  // Last fetched
  lastFetchedAt: number | null

  // Poll timer reference
  _pollTimer: ReturnType<typeof setInterval> | null
}

// ── Store Actions ───────────────────────────────────────────────────────────

interface SessionsStoreActions {
  // List operations
  loadList(): Promise<void>
  loadMore(): Promise<void>
  setFilters(filters: SessionFilters): void
  clearFilters(): void

  // Selection
  selectSession(id: string | null): void

  // Comparison mode (task 2621)
  toggleComparisonSelection(id: string, metaKey: boolean): void
  clearComparison(): void
  setComparisonScrollLinked(linked: boolean): void

  // Tab management
  setActiveTab(tab: SessionInspectorTab): void

  // Transcript lazy-loading
  /**
   * Load the transcript for the given session.
   *
   * The canonical `/history` endpoint returns the full event list in a single
   * round-trip, so the `customLimit` argument is accepted for backwards
   * compatibility but currently ignored.  Pagination may be re-introduced
   * later by adding cursor support to the backend handler.
   *
   * The owning `agentId` is resolved automatically: looked up in
   * `state.sessions` first, and otherwise fetched on-demand via
   * `resolveSession(sessionId)` so callers that hold only a sessionId (e.g.
   * the Runs-tab transcript modal) work without needing to mount the
   * Sessions Browser first.  Errors land in the transcript cache so the UI
   * can surface them.
   *
   * @param sessionId - Session to load.
   * @param customLimit - Reserved; ignored for now.
   */
  /**
   * @param sessionId  Session to load.
   * @param customLimit  Reserved; ignored for now.
   * @param userIdHint  Owning user_id, if the caller already knows it (e.g.
   *   extracted from an automation step's output map).  Used as a fallback
   *   for the `?user_id=` query param on /history when the resolved session
   *   record doesn't carry user_id.  This avoids the slow candidate-loop
   *   probe inside the agents backend's `_resolve_session` helper, which is
   *   the dominant source of the ~12s "View session" stall on the Runs tab.
   * @param agentIdHint  Owning agent_id, if the caller already knows it (e.g.
   *   extracted from an automation step's input map).  When resolveSession
   *   returns null — which can happen for newly-dispatched sessions whose
   *   metadata hasn't propagated to the global sessions listing yet — this
   *   hint is used to synthesise a minimal session record and attempt a
   *   direct /history load instead of immediately entering the error state.
   */
  loadTranscriptChunk(
    sessionId: string,
    customLimit?: number,
    userIdHint?: string | null,
    agentIdHint?: string | null,
  ): Promise<void>

  /**
   * Invalidate the cached transcript for a session.
   *
   * Drops the cache entry so the next `loadTranscriptChunk(sessionId)` call
   * re-fetches the full transcript from `/history`. Used when something
   * external to the modal (e.g. a tool-approval rejection in the queue) is
   * known to have produced new turns server-side. Without this, the cache
   * status of `'complete'` would otherwise short-circuit the loader and
   * the modal would keep showing the stale transcript.
   *
   * If `reload` is true and the session is currently selected (or pre-warmed),
   * a refetch is fired immediately. Otherwise the cache is just dropped and
   * the next opener triggers the refetch.
   */
  invalidateTranscript(sessionId: string, reload?: boolean): void

  // Artifact & trace loading.  Both resolve the owning agentId from
  // sessionId on-demand when the session record is not already cached.
  loadArtifacts(sessionId: string): Promise<void>
  loadTrace(sessionId: string): Promise<void>

  // In-transcript search
  setTranscriptSearch(query: string): void
  nextMatch(): void
  prevMatch(): void
  clearTranscriptSearch(): void

  // Agents facet
  loadAgents(): Promise<void>

  // URL sync
  hydrateFromUrl(searchParams: URLSearchParams): void
  serializeToUrl(): URLSearchParams

  // Polling
  startPolling(): void
  stopPolling(): void
}

type SessionsStore = SessionsStoreState & SessionsStoreActions

// ── Initial State ───────────────────────────────────────────────────────────

const emptyTranscriptSearch: TranscriptSearchState = {
  query: '',
  matches: [],
  currentMatchIndex: 0,
}

const initialState: SessionsStoreState = {
  sessions: [],
  filteredSessions: [],
  groupedSessions: [],
  cursor: null,
  hasMore: false,
  // Task 3115: include_subagents defaults to false — subagent sessions are
  // hidden by default to keep the list clean.
  filters: { include_subagents: false },
  sortState: { column: 'startedAt', direction: 'desc' },
  selectedId: null,
  comparisonIds: null,
  comparisonScrollLinked: true,
  transcriptCache: new Map(),
  artifactsCache: new Map(),
  traceCache: new Map(),
  activeTab: 'transcript',
  transcriptSearch: emptyTranscriptSearch,
  listLoading: false,
  listError: null,
  loadingMore: false,
  agents: [],
  agentsLoading: false,
  lastFetchedAt: null,
  _pollTimer: null,
}

// ── Helper: Apply client-side filters ───────────────────────────────────────

/**
 * Apply the active filters to a session list in-process.
 *
 * Mirrors `applyClientFilters` in the Automations store (task 3201e). The
 * server already narrows by these fields when given the corresponding query
 * params, but we re-apply client-side so that:
 *
 *   1. Subsequent ``loadMore`` pages stay narrowed to the active search
 *      term even if the server's `q` semantics are imperfect.
 *   2. The operator gets instant local feedback the moment they type into
 *      the search box, before the network round-trip completes.
 *
 * The ``q`` substring match is broad (matches the Automations behaviour):
 * case-insensitive substring against every text field the row component
 * displays — agent name, session id, status label, source label, and the
 * user identifier columns — so the operator's intuition that "what I see
 * is what I can search" holds.
 */
export function applyClientFilters(
  sessions: SessionListItem[],
  filters: SessionFilters
): SessionListItem[] {
  let result = sessions

  // Text search (broad coverage across surface-relevant fields).
  if (filters.q) {
    const q = filters.q.toLowerCase()
    result = result.filter((s) => {
      const haystacks: Array<string | undefined> = [
        s.agentName,
        s.agentId,
        s.sessionId,
        s.status,
        s.source,
        s.userEmail,
        s.userId,
        // tags are an array — fold into a single searchable string
        s.tags?.join(' '),
      ]
      return haystacks.some(
        (h) => h !== undefined && h.toLowerCase().includes(q)
      )
    })
  }

  // Facet: status
  if (filters.status && filters.status.length > 0) {
    result = result.filter((s) => filters.status!.includes(s.status))
  }

  // Facet: source
  if (filters.source && filters.source.length > 0) {
    result = result.filter((s) => filters.source!.includes(s.source))
  }

  // Facet: agent_id
  if (filters.agent_id && filters.agent_id.length > 0) {
    result = result.filter((s) => filters.agent_id!.includes(s.agentId))
  }

  // Facet: user_id (exact match — operator picks from a known identifier)
  if (filters.user_id) {
    const uid = filters.user_id
    result = result.filter((s) => s.userId === uid)
  }

  // Facet: min duration (seconds)
  if (filters.min_duration_s !== undefined) {
    const minMs = filters.min_duration_s * 1000
    result = result.filter(
      (s) => s.durationMs !== undefined && s.durationMs >= minMs
    )
  }

  // Facet: min cost (USD)
  if (filters.min_cost_usd !== undefined) {
    const minCost = filters.min_cost_usd
    result = result.filter(
      (s) => s.totalCostUsd !== undefined && s.totalCostUsd >= minCost
    )
  }

  // Facet: from / to (ISO date range on startedAt)
  if (filters.from) {
    const fromTs = new Date(filters.from).getTime()
    if (!Number.isNaN(fromTs)) {
      result = result.filter((s) => new Date(s.startedAt).getTime() >= fromTs)
    }
  }
  if (filters.to) {
    const toTs = new Date(filters.to).getTime()
    if (!Number.isNaN(toTs)) {
      result = result.filter((s) => new Date(s.startedAt).getTime() <= toTs)
    }
  }

  // Facet: include_subagents.  Defaults to false — hide A2A subagent
  // sessions (those carrying ``parentSessionId``) unless the operator
  // opted in.  Mirrors the server-side default.
  if (!filters.include_subagents) {
    result = result.filter((s) => !s.parentSessionId)
  }

  return result
}

// ── Helper: Compute transcript search matches ────────────────────────────────

function computeSearchMatches(
  turns: TranscriptTurnItem[],
  query: string
): number[] {
  if (!query.trim()) return []

  const q = query.toLowerCase()
  return turns
    .filter((turn) => {
      const contentStr =
        typeof turn.content === 'string'
          ? turn.content
          : JSON.stringify(turn.content)
      const toolStr = turn.toolName ?? ''
      return (
        contentStr.toLowerCase().includes(q) ||
        toolStr.toLowerCase().includes(q)
      )
    })
    .map((turn) => turn.turnIndex)
}

// ── Store Implementation ────────────────────────────────────────────────────

export const useSessionsStore = create<SessionsStore>((set, get) => ({
  ...initialState,

  loadList: async () => {
    set({ listLoading: true, listError: null })

    try {
      const state = get()
      const response = await listSessions({
        filters: state.filters,
        limit: LIST_PAGE_SIZE,
      })

      // Re-apply the active filters client-side so the visible list stays
      // tight to the search term even if the server's `q` semantics are
      // imperfect or partial.  Task 3201e.
      const filtered = applyClientFilters(response.sessions, state.filters)
      const grouped = groupSessionsByBucket(filtered)

      set({
        sessions: response.sessions,
        filteredSessions: filtered,
        groupedSessions: grouped,
        cursor: response.cursor ?? null,
        hasMore: response.hasMore,
        listLoading: false,
        lastFetchedAt: Date.now(),
      })

      // Pre-warm transcript cache for the most-recent sessions so clicking
      // them feels instant.  Fire-and-forget — errors are absorbed inside
      // loadTranscriptChunk.  We skip sessions already in the cache so polls
      // don't trigger redundant network requests.
      if (PRE_WARM_TRANSCRIPT_COUNT > 0) {
        const topSessions = response.sessions.slice(0, PRE_WARM_TRANSCRIPT_COUNT)
        const currentCache = get().transcriptCache
        for (const session of topSessions) {
          if (!currentCache.has(session.sessionId)) {
            void get().loadTranscriptChunk(session.sessionId)
          }
        }
      }
    } catch (error) {
      set({
        listLoading: false,
        listError:
          error instanceof Error ? error.message : 'Failed to load sessions',
      })
    }
  },

  loadMore: async () => {
    const state = get()
    if (state.loadingMore || !state.hasMore || !state.cursor) return

    set({ loadingMore: true })

    try {
      const response = await listSessions({
        filters: state.filters,
        cursor: state.cursor,
        limit: LIST_PAGE_SIZE,
      })

      const allSessions = [...state.sessions, ...response.sessions]
      // Re-apply client-side filters across the merged set so newly
      // loaded rows that don't match the active search term don't bleed
      // into the visible list (the Load-More-grows-with-junk bug from
      // task 3201e).
      const filtered = applyClientFilters(allSessions, state.filters)
      const grouped = groupSessionsByBucket(filtered)

      set({
        sessions: allSessions,
        filteredSessions: filtered,
        groupedSessions: grouped,
        cursor: response.cursor ?? null,
        hasMore: response.hasMore,
        loadingMore: false,
      })
    } catch (error) {
      set({
        loadingMore: false,
        listError: error instanceof Error ? error.message : 'Failed to load more sessions',
      })
    }
  },

  setFilters: (filters) => {
    // Re-narrow the in-memory list immediately so the operator gets
    // instant local feedback while the server round-trip is in flight.
    // ``loadList`` will overwrite this with the freshly-fetched (and
    // re-narrowed) page once it resolves.  Task 3201e.
    const state = get()
    const filtered = applyClientFilters(state.sessions, filters)
    set({
      filters,
      filteredSessions: filtered,
      groupedSessions: groupSessionsByBucket(filtered),
    })
    get().loadList()
  },

  clearFilters: () => {
    const state = get()
    const cleared: SessionFilters = {}
    const filtered = applyClientFilters(state.sessions, cleared)
    set({
      filters: cleared,
      filteredSessions: filtered,
      groupedSessions: groupSessionsByBucket(filtered),
    })
    get().loadList()
  },

  selectSession: (id) => {
    const prev = get().selectedId
    set({
      selectedId: id,
      activeTab: 'transcript',
      transcriptSearch: emptyTranscriptSearch,
    })

    if (id && id !== prev) {
      // Kick off transcript load for the first chunk
      get().loadTranscriptChunk(id)
    }
  },

  toggleComparisonSelection: (id, metaKey) => {
    const state = get()

    if (!metaKey) {
      // Regular click — clear comparison, single-select
      set({ comparisonIds: null })
      get().selectSession(id)
      return
    }

    // Meta/Ctrl+click — toggle comparison selection
    if (state.comparisonIds) {
      // Already comparing: if clicking one of the two, remove it and revert to single-select
      const [a, b] = state.comparisonIds
      if (id === a) {
        set({ comparisonIds: null })
        get().selectSession(b)
      } else if (id === b) {
        set({ comparisonIds: null })
        get().selectSession(a)
      } else {
        // Clicking a third session: replace the second
        set({ comparisonIds: [a, id] })
        // Load transcript for the new session
        get().loadTranscriptChunk(id)
      }
    } else if (state.selectedId && state.selectedId !== id) {
      // First meta-click with an existing selection: enter comparison
      set({ comparisonIds: [state.selectedId, id] })
      // Load transcript for the second session
      get().loadTranscriptChunk(id)
    } else {
      // No prior selection or same ID — just select
      get().selectSession(id)
    }
  },

  clearComparison: () => {
    const state = get()
    const firstId = state.comparisonIds?.[0] ?? null
    set({ comparisonIds: null, comparisonScrollLinked: true })
    if (firstId) get().selectSession(firstId)
  },

  setComparisonScrollLinked: (linked) => {
    set({ comparisonScrollLinked: linked })
  },

  setActiveTab: (tab) => {
    set({ activeTab: tab })

    const state = get()
    if (!state.selectedId) return

    // The agentId is resolved on-demand by loadArtifacts / loadTrace if the
    // session isn't in `state.sessions` yet (e.g. when the Runs-tab modal
    // sets the selected id but the Sessions Browser list hasn't loaded).
    if (tab === 'artifacts') {
      get().loadArtifacts(state.selectedId)
    } else if (tab === 'trace') {
      get().loadTrace(state.selectedId)
    }
  },

  loadTranscriptChunk: async (sessionId, customLimit, userIdHint, agentIdHint) => {
    const state = get()
    const cached = state.transcriptCache.get(sessionId)

    // Already complete or actively loading
    if (cached?.status === 'complete' || cached?.status === 'loading') return

    // Resolve the agent id from the loaded session list.  If the session
    // isn't cached locally (e.g. the operator opened the transcript modal
    // from the Runs tab without ever mounting the Sessions Browser), fetch
    // the metadata on-demand via the `session_id` filter on the listing
    // endpoint and merge the result into `state.sessions` so subsequent
    // loaders hit the fast path.
    let session = state.sessions.find((s) => s.sessionId === sessionId)
    if (!session) {
      // Mark as loading immediately so the modal shows its skeleton instead
      // of an empty pane while the resolve round-trip is in flight.
      set((s) => {
        const newCache = new Map(s.transcriptCache)
        const existing = newCache.get(sessionId)
        newCache.set(sessionId, {
          turns: existing?.turns ?? [],
          nextAfterTurn: existing?.nextAfterTurn,
          status: 'loading',
        })
        return { transcriptCache: newCache }
      })
      try {
        const resolved = await resolveSession(sessionId)
        if (!resolved) {
          // resolveSession can return null for newly-dispatched sessions
          // whose metadata hasn't propagated to the global sessions listing
          // yet (e.g. a Cloud Run Job that was just dispatched).  When the
          // caller supplied an agentIdHint (extracted from the step input),
          // synthesise a minimal session record and proceed — the /history
          // endpoint is the authoritative source and will return the turns
          // (or empty) regardless of whether the listing is stale.
          // Without a hint we have no agentId to build the /history URL, so
          // we fall through to the error state as before.
          if (agentIdHint) {
            const now = new Date().toISOString()
            session = {
              sessionId,
              agentId: agentIdHint,
              agentName: agentIdHint,
              userId: (userIdHint && userIdHint.length > 0) ? userIdHint : undefined,
              status: 'running' as const,
              source: 'production' as const,
              startedAt: now,
              updatedAt: now,
              turnCount: 0,
            }
            // Merge the synthesised record into state.sessions so that
            // subsequent loadTranscriptChunk calls triggered by
            // invalidateTranscript (e.g. from the live-stream polling
            // fallback) can resolve the agentId via state.sessions.find()
            // without needing the hint to be re-supplied.  Without this,
            // every poll call loses the agentId, falls through to
            // resolveSession → null → "Session not found." error, and
            // overwrites the transcript cache for the entire duration of
            // the agent run.
            const synthRecord = session
            set((s) => {
              if (s.sessions.some((x) => x.sessionId === sessionId)) return s
              return { sessions: [...s.sessions, synthRecord] }
            })
          } else {
            set((s) => {
              const newCache = new Map(s.transcriptCache)
              newCache.set(sessionId, {
                turns: [],
                status: 'error',
                error: 'Session not found.',
              })
              return { transcriptCache: newCache }
            })
            return
          }
        } else {
          // Merge the resolved record so artifacts / trace / repeat opens
          // skip the lookup.
          set((s) => {
            if (s.sessions.some((x) => x.sessionId === sessionId)) return s
            return { sessions: [...s.sessions, resolved] }
          })
          session = resolved
        }
      } catch (error) {
        set((s) => {
          const newCache = new Map(s.transcriptCache)
          newCache.set(sessionId, {
            turns: [],
            status: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Failed to resolve session metadata',
          })
          return { transcriptCache: newCache }
        })
        return
      }
    }
    // At this point `session` is always defined — all non-assignment paths
    // inside the `if (!session)` block above call `return`.  TypeScript's
    // control-flow analysis can't see through the async try-catch, so we
    // assert rather than add a dead-code branch.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const resolvedSession = session!
    const agentId = resolvedSession.agentId
    const fallbackTimestamp = resolvedSession.startedAt

    // Determine afterTurn for pagination
    const afterTurn =
      cached?.nextAfterTurn != null
        ? cached.nextAfterTurn
        : undefined

    // Mark as loading
    set((s) => {
      const newCache = new Map(s.transcriptCache)
      const existing = newCache.get(sessionId)
      newCache.set(sessionId, {
        turns: existing?.turns ?? [],
        nextAfterTurn: existing?.nextAfterTurn,
        status: 'loading',
      })
      return { transcriptCache: newCache }
    })

    // Resolve user_id for the /history fast path.  Prefer the value on the
    // resolved session record (populated either from the local list or
    // from the `session_id_filter` SQL lookup inside `resolveSession`).
    // Fall back to the caller-supplied hint (e.g. the invoke_agent step's
    // output `user_id`) if the session record didn't carry one.  Without
    // either, the backend falls into the candidate-loop probe — fine as a
    // safety net but ~12s slower than the partition-key lookup.
    const userIdForHistory =
      (resolvedSession.userId && resolvedSession.userId.length > 0 ? resolvedSession.userId : null) ??
      (userIdHint && userIdHint.length > 0 ? userIdHint : null) ??
      undefined

    try {
      const chunk = await getTranscriptChunk(agentId, sessionId, {
        afterTurn,
        // Allow callers to request a larger initial batch (e.g. the
        // AgentSessionModal in the queue).  Currently ignored by the
        // `/history` adapter — the full transcript is returned in one
        // round-trip — but preserved on the type signature for when
        // pagination is re-introduced.
        limit: customLimit ?? TRANSCRIPT_PAGE_SIZE,
        fallbackTimestamp,
        userId: userIdForHistory,
      })

      // Defensive: ensure chunk.turns is an array
      if (!chunk || !Array.isArray(chunk.turns)) {
        throw new Error('Invalid transcript response: missing or malformed turns array')
      }

      set((s) => {
        const newCache = new Map(s.transcriptCache)
        const existing = newCache.get(sessionId)

        // Deduplicate turns by turnIndex to prevent duplicate rendering
        const existingTurns = existing?.turns ?? []
        const existingIndices = new Set(existingTurns.map((t) => t.turnIndex))
        const newTurns = chunk.turns.filter((t) => !existingIndices.has(t.turnIndex))
        const allTurns = [...existingTurns, ...newTurns]

        // Recompute search matches if search is active
        const searchQuery = s.transcriptSearch.query
        const matches = computeSearchMatches(allTurns, searchQuery)

        newCache.set(sessionId, {
          turns: allTurns,
          nextAfterTurn: chunk.nextAfterTurn ?? undefined,
          status: chunk.nextAfterTurn == null ? 'complete' : 'loaded',
        })

        return {
          transcriptCache: newCache,
          transcriptSearch: {
            ...s.transcriptSearch,
            matches,
          },
        }
      })
    } catch (error) {
      // HTTP 404 means the ADK session hasn't been committed to the DB yet
      // (race between the agent dispatch and our first fetch).  Treat this as
      // a transient "not ready yet" condition rather than a hard failure:
      //   - If we already have turns from a previous successful load, keep
      //     them visible (status → 'loaded') so the operator doesn't see the
      //     error overlay on existing content.
      //   - If we have no turns yet, keep the loading skeleton (status →
      //     'loading') — polling will retry every 5 seconds and the
      //     transcript will appear once the session materialises.
      // Only surface a hard error (red warning card) for non-404 failures.
      const is404 = error instanceof ApiClientError && error.statusCode === 404
      set((s) => {
        const newCache = new Map(s.transcriptCache)
        const existing = newCache.get(sessionId)
        const existingTurns = existing?.turns ?? []
        newCache.set(sessionId, {
          turns: existingTurns,
          nextAfterTurn: existing?.nextAfterTurn,
          status: is404
            ? existingTurns.length > 0
              ? 'loaded'
              : 'loading'
            : 'error',
          error: is404
            ? undefined
            : error instanceof Error
              ? error.message
              : 'Failed to load transcript',
        })
        return { transcriptCache: newCache }
      })
    }
  },

  invalidateTranscript: (sessionId, reload = true) => {
    if (!sessionId) return
    // Downgrade status from 'complete' → 'loaded' so the next
    // loadTranscriptChunk call is NOT blocked by the "already complete"
    // short-circuit guard (line: `if (cached?.status === 'complete') return`).
    // Without this, any live-stream tick that fires invalidateTranscript
    // after the transcript has fully loaded is silently ignored — the guard
    // returns before making a network request, so new turns (e.g. post-
    // tool-rejection turns) never appear.
    //
    // We intentionally preserve existing turns and do NOT clear the cache
    // entry: loadTranscriptChunk keeps existing?.turns during its own
    // 'loading' phase, so turns stay visible throughout the refresh cycle
    // and there is no skeleton flash.
    set((s) => {
      const existing = s.transcriptCache.get(sessionId)
      if (!existing || existing.status !== 'complete') return s
      const newCache = new Map(s.transcriptCache)
      newCache.set(sessionId, { ...existing, status: 'loaded' })
      return { transcriptCache: newCache }
    })
    if (reload) {
      // Fire-and-forget; errors land in the cache and surface via the modal.
      void get().loadTranscriptChunk(sessionId)
    }
  },

  loadArtifacts: async (sessionId) => {
    const state = get()
    const cached = state.artifactsCache.get(sessionId)

    if (cached?.status === 'loading' || cached?.status === 'loaded') return

    set((s) => {
      const newCache = new Map(s.artifactsCache)
      newCache.set(sessionId, { artifacts: [], status: 'loading' })
      return { artifactsCache: newCache }
    })

    // Resolve agentId via the session list, falling back to the
    // sessionId-only listing endpoint when the session isn't cached.
    let session = state.sessions.find((s) => s.sessionId === sessionId)
    if (!session) {
      try {
        const resolved = await resolveSession(sessionId)
        if (!resolved) {
          set((s) => {
            const newCache = new Map(s.artifactsCache)
            newCache.set(sessionId, {
              artifacts: [],
              status: 'error',
              error: 'Session not found.',
            })
            return { artifactsCache: newCache }
          })
          return
        }
        set((s) => {
          if (s.sessions.some((x) => x.sessionId === sessionId)) return s
          return { sessions: [...s.sessions, resolved] }
        })
        session = resolved
      } catch (error) {
        set((s) => {
          const newCache = new Map(s.artifactsCache)
          newCache.set(sessionId, {
            artifacts: [],
            status: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Failed to resolve session metadata',
          })
          return { artifactsCache: newCache }
        })
        return
      }
    }
    const agentId = session.agentId

    try {
      const response = await getSessionArtifacts(agentId, sessionId)

      set((s) => {
        const newCache = new Map(s.artifactsCache)
        newCache.set(sessionId, { artifacts: response.artifacts, status: 'loaded' })
        return { artifactsCache: newCache }
      })
    } catch (error) {
      set((s) => {
        const newCache = new Map(s.artifactsCache)
        newCache.set(sessionId, {
          artifacts: [],
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to load artifacts',
        })
        return { artifactsCache: newCache }
      })
    }
  },

  loadTrace: async (sessionId) => {
    const state = get()
    const cached = state.traceCache.get(sessionId)

    if (cached?.status === 'loading' || cached?.status === 'loaded') return

    // Resolve agent id from the loaded session list — `/history` is a
    // per-agent endpoint.  If the session isn't cached locally, fetch its
    // metadata on-demand via the listing endpoint's `session_id` filter
    // rather than failing with "retry once the list appears".
    let session = state.sessions.find((s) => s.sessionId === sessionId)
    if (!session) {
      set((s) => {
        const newCache = new Map(s.traceCache)
        newCache.set(sessionId, { data: null, status: 'loading' })
        return { traceCache: newCache }
      })
      try {
        const resolved = await resolveSession(sessionId)
        if (!resolved) {
          set((s) => {
            const newCache = new Map(s.traceCache)
            newCache.set(sessionId, {
              data: null,
              status: 'error',
              error: 'Session not found.',
            })
            return { traceCache: newCache }
          })
          return
        }
        set((s) => {
          if (s.sessions.some((x) => x.sessionId === sessionId)) return s
          return { sessions: [...s.sessions, resolved] }
        })
        session = resolved
      } catch (error) {
        set((s) => {
          const newCache = new Map(s.traceCache)
          newCache.set(sessionId, {
            data: null,
            status: 'error',
            error:
              error instanceof Error
                ? error.message
                : 'Failed to resolve session metadata',
          })
          return { traceCache: newCache }
        })
        return
      }
    }
    const agentId = session.agentId

    set((s) => {
      const newCache = new Map(s.traceCache)
      newCache.set(sessionId, { data: null, status: 'loading' })
      return { traceCache: newCache }
    })

    try {
      const data = await getSessionTrace(agentId, sessionId)

      set((s) => {
        const newCache = new Map(s.traceCache)
        newCache.set(sessionId, { data, status: 'loaded' })
        return { traceCache: newCache }
      })
    } catch (error) {
      set((s) => {
        const newCache = new Map(s.traceCache)
        newCache.set(sessionId, {
          data: null,
          status: 'error',
          error: error instanceof Error ? error.message : 'Failed to load trace',
        })
        return { traceCache: newCache }
      })
    }
  },

  setTranscriptSearch: (query) => {
    const state = get()
    if (!state.selectedId) return

    const cached = state.transcriptCache.get(state.selectedId)
    const turns = cached?.turns ?? []
    const matches = computeSearchMatches(turns, query)

    set({
      transcriptSearch: { query, matches, currentMatchIndex: 0 },
    })
  },

  nextMatch: () => {
    set((s) => {
      const { matches, currentMatchIndex } = s.transcriptSearch
      if (matches.length === 0) return s
      const next = (currentMatchIndex + 1) % matches.length
      return {
        transcriptSearch: { ...s.transcriptSearch, currentMatchIndex: next },
      }
    })
  },

  prevMatch: () => {
    set((s) => {
      const { matches, currentMatchIndex } = s.transcriptSearch
      if (matches.length === 0) return s
      const prev = (currentMatchIndex - 1 + matches.length) % matches.length
      return {
        transcriptSearch: { ...s.transcriptSearch, currentMatchIndex: prev },
      }
    })
  },

  clearTranscriptSearch: () => {
    set({ transcriptSearch: emptyTranscriptSearch })
  },

  loadAgents: async () => {
    const current = get().agents ?? []
    if (get().agentsLoading || current.length > 0) return

    set({ agentsLoading: true })

    try {
      const response = await listAgents()
      // Defensive: backend may return data as null or undefined when there are
      // no agents. Coerce to [] so consumers don't crash on .length / .map.
      set({ agents: response?.data ?? [], agentsLoading: false })
    } catch {
      set({ agentsLoading: false })
    }
  },

  hydrateFromUrl: (searchParams) => {
    const filters: SessionFilters = {}

    const q = searchParams.get('q')
    if (q) filters.q = q

    const agentId = searchParams.get('agent_id')
    if (agentId) filters.agent_id = agentId.split(',')

    const status = searchParams.get('status')
    if (status) filters.status = status.split(',') as SessionStatus[]

    const source = searchParams.get('source')
    if (source) filters.source = source.split(',') as SessionSource[]

    const userId = searchParams.get('user_id')
    if (userId) filters.user_id = userId

    const from = searchParams.get('from')
    if (from) filters.from = from

    const to = searchParams.get('to')
    if (to) filters.to = to

    const minDuration = searchParams.get('min_duration_s')
    const minDurationNum = minDuration !== null ? Number(minDuration) : NaN
    if (!Number.isNaN(minDurationNum) && minDuration !== '') filters.min_duration_s = minDurationNum

    const minCost = searchParams.get('min_cost_usd')
    const minCostNum = minCost !== null ? Number(minCost) : NaN
    if (!Number.isNaN(minCostNum) && minCost !== '') filters.min_cost_usd = minCostNum

    // Task 3115: include_subagents URL param ('1' = true, absent = false).
    const includeSubagents = searchParams.get('include_subagents')
    filters.include_subagents = includeSubagents === '1'

    const selected = searchParams.get('selected')
    const tab = searchParams.get('tab') as SessionInspectorTab | null

    // Comparison mode (task 2621)
    const compare = searchParams.get('compare')
    let comparisonIds: [string, string] | null = null
    if (compare) {
      const parts = compare.split(',')
      if (parts.length === 2 && parts[0] && parts[1]) {
        comparisonIds = [parts[0], parts[1]]
      }
    }

    set({
      filters,
      selectedId: selected ?? comparisonIds?.[0] ?? null,
      activeTab: tab ?? 'transcript',
      comparisonIds,
    })

    // Load transcripts for comparison sessions
    if (comparisonIds) {
      get().loadTranscriptChunk(comparisonIds[0])
      get().loadTranscriptChunk(comparisonIds[1])
    } else if (selected) {
      // Load transcript for single selected session (deep-link or URL hydration)
      get().loadTranscriptChunk(selected)
    }
  },

  serializeToUrl: () => {
    const state = get()
    const params = new URLSearchParams()

    const f = state.filters
    if (f.q) params.set('q', f.q)
    if (f.agent_id?.length) params.set('agent_id', f.agent_id.join(','))
    if (f.status?.length) params.set('status', f.status.join(','))
    if (f.source?.length) params.set('source', f.source.join(','))
    if (f.user_id) params.set('user_id', f.user_id)
    if (f.from) params.set('from', f.from)
    if (f.to) params.set('to', f.to)
    if (f.min_duration_s !== undefined)
      params.set('min_duration_s', String(f.min_duration_s))
    if (f.min_cost_usd !== undefined)
      params.set('min_cost_usd', String(f.min_cost_usd))
    // Task 3115: only write include_subagents to URL when it's true (default
    // false keeps URLs clean).
    if (f.include_subagents) params.set('include_subagents', '1')

    if (state.comparisonIds) {
      params.set('compare', state.comparisonIds.join(','))
    } else if (state.selectedId) {
      params.set('selected', state.selectedId)
    }
    if (state.activeTab !== 'transcript') params.set('tab', state.activeTab)

    return params
  },

  startPolling: () => {
    const state = get()
    if (state._pollTimer) return

    const timer = setInterval(() => {
      get().loadList()
    }, POLL_INTERVAL_MS)

    set({ _pollTimer: timer })
  },

  stopPolling: () => {
    const state = get()
    if (state._pollTimer) {
      clearInterval(state._pollTimer)
      set({ _pollTimer: null })
    }
  },
}))

// ── Selectors ───────────────────────────────────────────────────────────────

/**
 * Select the transcript cache entry for the currently selected session.
 */
export const selectCurrentTranscript = (
  state: SessionsStore
): TranscriptCache | null => {
  if (!state.selectedId) return null
  return state.transcriptCache.get(state.selectedId) ?? null
}

/**
 * Select the currently selected session item from the list.
 */
export const selectCurrentSession = (
  state: SessionsStore
): SessionListItem | null => {
  if (!state.selectedId) return null
  return state.sessions.find((s) => s.sessionId === state.selectedId) ?? null
}

/**
 * Check if any filters are active.
 */
export const selectHasActiveFilters = (state: SessionsStore): boolean => {
  const f = state.filters
  return !!(
    f.q ||
    f.agent_id?.length ||
    f.status?.length ||
    f.source?.length ||
    f.user_id ||
    f.from ||
    f.to ||
    f.min_duration_s !== undefined ||
    f.min_cost_usd !== undefined ||
    f.include_subagents
  )
}

/**
 * Check if comparison mode is active (task 2621).
 */
export const selectIsComparing = (state: SessionsStore): boolean =>
  state.comparisonIds !== null
